"use client";

import { useEffect, useRef } from "react";
import { footerLinks } from "@/config/site";
import { track } from "@/lib/stats-client";
import { useSitePath } from "@/components/ui/use-site-path";

// Counts the site's page views by kind (lib/stats.ts), and clicks on links marked data-stat:
// "room:<site>:<place>" (a cam room), "telegram:<place>", "creator:<place>", "game:<place>[:<theme>]" (Sweet Room),
// "escorts:<place>" (the escorts directory), "partner:<offer>:<place>" (a partner's offer: lib/offers).

const BROWSE = new Set(["categories", "people", "brands", "search", "rankings", "subscription", "watching", "saved", "following", "shorts"]);
const LEGAL = new Set<string>(footerLinks.map((l) => l.href.slice(1)));

/** What kind of page a path is, as the counters name it: "home", "cams:stripchat", "model:cam4", "video"… */
export function pageKind(pathname: string): string {
  if (pathname === "/") return "home";
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "cams") return parts.length > 2 ? `model:${parts[1]}` : `cams:${parts[1] ?? ""}`;
  if (parts[0] === "videos") return "videos";
  if (parts[0] === "categories" && parts.length > 1) return "category";
  if (parts[0] === "live") return `live:${parts[1] ?? ""}`;
  if (parts[0]?.startsWith("-")) return "video";
  // The sex stories' list and each story (app/[lang]/(site)/stories), and the sections (VR, Cartoon, Hentai).
  if (parts[0] === "stories") return parts.length > 1 && parts[1] !== "submit" ? "story" : parts[1] === "submit" ? "storysubmit" : "stories";
  if (parts.length === 1 && (parts[0] === "vr" || parts[0] === "cartoon" || parts[0] === "hentai")) return parts[0];
  // Sweet AI and the premium porn sites (lib/offers).
  if (parts.length === 1 && parts[0] === "ai") return "ai";
  if (parts.length === 1 && parts[0] === "premium-porn-sites") return "paysites";
  if (parts.length === 1 && BROWSE.has(parts[0])) return parts[0];
  if (parts.length === 1 && LEGAL.has(parts[0])) return "legal";
  return parts.length === 1 ? "profile" : "other";
}

export function PageStats() {
  const pathname = useSitePath();
  // Once per page (development runs effects twice).
  const counted = useRef<string | null>(null);

  useEffect(() => {
    if (counted.current === pathname) return;
    counted.current = pathname;
    const kind = pageKind(pathname).replace(/[^a-z0-9:]/g, "");
    if (kind) track("view", kind);
  }, [pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target.closest("[data-stat]") : null;
      const [what, a, b] = (el?.getAttribute("data-stat") ?? "").split(":");
      if (what === "room" && a) {
        track("room", a);
        if (b) track("room_place", b);
      } else if ((what === "telegram" || what === "creator") && a) track(what, a);
      else if (what === "game" && a) track("game_link", b ? `${a}:${b}` : a);
      else if (what === "escorts" && a) track("escorts_link", a);
      else if (what === "partner" && a) {
        track("partner", a);
        if (b) track("partner_place", b);
      }
      // A Sweet Boost's promoted item (components/boost/Promoted.tsx), whatever link in it was clicked.
      const boost = e.target instanceof Element ? e.target.closest("[data-boost]")?.getAttribute("data-boost") : null;
      if (boost) track("boost_click", boost);
    };
    // Capturing, and for middle clicks too: links open in new tabs.
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, []);

  return null;
}
